import React, { useState } from 'react';
import { experiences, otherExperiences } from '../../data/experience';
import { recommendations } from '../../data/recommendations';
import { calculateDuration } from '../../utils/date';
import Card from '../common/Card';
import styles from './Experience.module.css';

const Experience: React.FC = () => {
  const [otherOpen, setOtherOpen] = useState(false);

  return (
      <section className={styles.experience}>
        <div className="container">
          <div className={styles.header}>
            <h2 className={styles.sectionTitle}>Expériences</h2>
          </div>

          <div className={styles.timeline}>
            {experiences.map((exp) => {
              const startDate = new Date(exp.startDate);
              const endDate = exp.endDate ? new Date(exp.endDate) : null;

              const formatDate = (date: Date) => {
                const month = date.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase();
                const year = date.getFullYear();
                return `${month}. ${year}`;
              };

              const period = endDate
                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                : `DEPUIS ${formatDate(startDate)}`;

              const duration = calculateDuration(exp.startDate, exp.endDate);

              return (
                  <div key={exp.id} className={styles.timelineItem}>
                    <div className={styles.meta}>
                      <div className={styles.metaItem}>{period}</div>
                    </div>
                    <div className={styles.timelineContent}>
                      <div className={styles.experienceHeader}>
                        <div className={styles.company}>{exp.company}</div>
                        <h3 className={styles.role}>{exp.role}</h3>
                        <div className={styles.metaInfo}>
                          <span>{duration}</span>
                          <span className={styles.separator}>·</span>
                          <span>{exp.location}</span>
                        </div>
                      </div>

                      <div
                          className={styles.description}
                          dangerouslySetInnerHTML={{__html: exp.description}}
                      />

                      {exp.skills && exp.skills.length > 0 && (
                          <div className={styles.skills}>
                            <strong className={styles.skillsLabel}>Compétences :</strong>
                            {exp.skills.map((skill, index) => (
                                <span key={index} className={styles.skillTag}>
                          {skill}
                        </span>
                            ))}
                          </div>
                      )}
                    </div>
                  </div>
              );
            })}
          </div>

          <div className={styles.formationSection}>
            <h3 className={styles.formationTitle}>Formation</h3>
            <div className={styles.formationContent}>
              <a
                href="https://parchment.procertif.com/019f4cb8-c5fc-73f1-aa9f-765122e7a9a4.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.formationItem}
              >
                <h4 className={styles.formationDegree}>
                  Conduire et piloter un projet innovant avec des méthodes agiles
                  <svg className={styles.externalLinkIcon} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 2H2a1 1 0 00-1 1v7a1 1 0 001 1h7a1 1 0 001-1V7M8 1h3m0 0v3m0-3L5 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </h4>
                <div className={styles.formationDetails}>
                  <span className={styles.formationField}>Certification RS6099</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.formationSchool}>ORIIONS</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.formationPeriod}>Juillet 2026</span>
                </div>
                <p className={styles.formationDescription}>
                  Certification obtenue à l'issue d'une formation Product Owner de 2 jours réalisée avec Cortisum en juin 2026.
                </p>
              </a>
              <a
                href="https://www.quasar-coaching.fr/boite-a-outils-du-manager-lille.html"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.formationItem}
              >
                <h4 className={styles.formationDegree}>
                  Formation "Boîte à outils du manager"
                  <svg className={styles.externalLinkIcon} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 2H2a1 1 0 00-1 1v7a1 1 0 001 1h7a1 1 0 001-1V7M8 1h3m0 0v3m0-3L5 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </h4>
                <div className={styles.formationDetails}>
                  <span className={styles.formationField}>2 jours</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.formationSchool}>Quasar Coaching</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.formationPeriod}>Octobre 2023</span>
                </div>
                <p className={styles.formationDescription}>
                  Fondamentaux du management opérationnel : cadrage, délégation, feedback, entretiens 1:1 et pilotage d'équipe. 75% de mises en situation pratiques.
                </p>
              </a>
              <div className={styles.formationItem}>
                <h4 className={styles.formationDegree}>Diplôme d'ingénieur</h4>
                <div className={styles.formationDetails}>
                  <span className={styles.formationField}>Informatique industrielle, microélectronique, automatisme</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.formationSchool}>Polytech Lille</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.formationPeriod}>2014 - 2020</span>
                </div>
              </div>
            </div>
          </div>

          {recommendations.length > 0 && (
            <div className={styles.recommendationsSection}>
              <h3 className={styles.recommendationsTitle}>Recommandations</h3>
              <div className={styles.recommendationsGrid}>
                {recommendations.map((rec) => (
                  <a
                    key={rec.id}
                    href={rec.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.recommendationCard}
                  >
                    <p className={styles.recommendationQuote}>{rec.text}</p>
                    <div className={styles.recommendationMeta}>
                      {rec.photo && (
                        <img
                          src={rec.photo}
                          alt={rec.author}
                          className={styles.recommendationPhoto}
                        />
                      )}
                      <div>
                        <span className={styles.recommendationAuthor}>{rec.author}</span>
                        <span className={styles.recommendationRelation}>{rec.relation} · {rec.date}</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {otherExperiences.length > 0 && (
              <div className={styles.otherExperiences}>
                <button
                  className={styles.otherToggle}
                  onClick={() => setOtherOpen(o => !o)}
                  aria-expanded={otherOpen}
                >
                  <span>Autres expériences</span>
                  <svg
                    className={`${styles.toggleIcon} ${otherOpen ? styles.toggleIconOpen : ''}`}
                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {otherOpen && (
                  <div className={styles.otherGrid}>
                    {otherExperiences.map((exp) => {
                      const duration = calculateDuration(exp.startDate, exp.endDate);
                      const year = new Date(exp.startDate).getFullYear();

                      return (
                          <Card
                              key={exp.id}
                              title={exp.role}
                              subtitle={`${exp.company} · ${duration} (${year})${exp.location ? ` · ${exp.location}` : ''}`}
                          >
                            <div
                                className={styles.description}
                                dangerouslySetInnerHTML={{__html: exp.description}}
                            />
                            {exp.skills && exp.skills.length > 0 && (
                                <div className={styles.skills}>
                                  <strong className={styles.skillsLabel}>Compétences :</strong>
                                  {exp.skills.map((skill, index) => (
                                      <span key={index} className={styles.skillTag}>
                                        {skill}
                                      </span>
                                  ))}
                                </div>
                            )}
                          </Card>
                      );
                    })}
                  </div>
                )}
              </div>
          )}
        </div>
      </section>

  );
};

export default Experience;
